import type { ScanResult } from "../../types/scan";
import OfflineDetector from "../../services/OfflineDetector";

const BACKEND_URL = process.env.REACT_APP_API_BACKEND_URL;

export const BTP_SCAN_TRACKED_FIELDS = [
  "firstname",
  "lastname",
  "company",
  "btp_number",
] as const;

export type BtpScanTrackedField = typeof BTP_SCAN_TRACKED_FIELDS[number];

export type BtpScanFieldValues = Record<BtpScanTrackedField, string>;

export type BtpScanBaseline = BtpScanFieldValues & {
  ocrTimedOut: boolean;
};

const normalize = (value: unknown): string =>
  typeof value === "string" ? value.trim() : value == null ? "" : String(value).trim();

export const buildBtpScanBaseline = (
  scan: ScanResult,
  companyValue: string | { name?: string } | null | undefined
): BtpScanBaseline => {
  const companyName =
    typeof companyValue === "string"
      ? companyValue
      : companyValue?.name ?? scan.company ?? "";

  return {
    firstname: normalize(scan.firstname),
    lastname: normalize(scan.lastname),
    company: normalize(companyName),
    btp_number: normalize(scan.btp_number),
    ocrTimedOut: Boolean(scan.ocrTimedOut),
  };
};

export const buildSubmittedScanValues = (values: {
  firstname?: string;
  lastname?: string;
  company?: string | { name?: string };
  btp?: string;
  btp_number?: string;
}): BtpScanFieldValues => ({
  firstname: normalize(values.firstname),
  lastname: normalize(values.lastname),
  company: normalize(
    typeof values.company === "string" ? values.company : values.company?.name
  ),
  btp_number: normalize(values.btp ?? values.btp_number),
});

export const computeCorrectedFields = (
  baseline: BtpScanFieldValues,
  submitted: BtpScanFieldValues
): BtpScanTrackedField[] =>
  BTP_SCAN_TRACKED_FIELDS.filter(
    (field) => normalize(baseline[field]) !== normalize(submitted[field])
  );

/** Fire-and-forget persistence of BTP recognition vs manual corrections. */
export const trackBtpCardScan = (payload: {
  token: string | null | undefined;
  workerId?: string | number | null;
  siteId?: string | number | null;
  baseline: BtpScanBaseline;
  submitted: BtpScanFieldValues;
  context: "create" | "update";
}): void => {
  if (!OfflineDetector.appIsOnline() || !payload.token || !BACKEND_URL) {
    return;
  }

  const scanned_values: BtpScanFieldValues = {
    firstname: payload.baseline.firstname,
    lastname: payload.baseline.lastname,
    company: payload.baseline.company,
    btp_number: payload.baseline.btp_number,
  };
  const corrected_fields = computeCorrectedFields(
    scanned_values,
    payload.submitted
  );
  const allScannedPresent = BTP_SCAN_TRACKED_FIELDS.every(
    (field) => scanned_values[field] !== ""
  );
  const is_fully_automatic =
    !payload.baseline.ocrTimedOut &&
    allScannedPresent &&
    corrected_fields.length === 0;

  const body = {
    worker_id: payload.workerId ?? null,
    site_id: payload.siteId ?? null,
    scanned_values,
    submitted_values: payload.submitted,
    corrected_fields,
    is_fully_automatic,
    ocr_timed_out: payload.baseline.ocrTimedOut,
    context: payload.context,
  };

  fetch(`${BACKEND_URL}/btp-card-scan`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${payload.token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  }).catch(() => {
    // Tracking must never block worker create/update.
  });
};
